import type { InputState } from './Physics';

export interface StickInputOptions {
  /** Called when any stick is first touched in a frame the racer was idle. */
  onAnyStart?: () => void;
}

interface Stick {
  side: 'left' | 'right';
  root: HTMLElement;
  thumb: HTMLElement;
  pointerId: number | null;
  // Anchor (the y in client coords representing thrust = 0). Recorded on
  // pointerdown so the user's first touch becomes the neutral position.
  // Floats if the player drags past max travel — same as PUBG/Genshin.
  anchorY: number;
  // Current vertical drag from anchor, in CSS pixels. Positive = drag up.
  // Sign convention: drag UP gives forward thrust, drag DOWN gives reverse.
  travelPx: number;
  // Travel at the moment of release; used by update() to ease back to 0.
  releaseAt: number | null;
  releaseStartTravel: number;
}

/** Max stick travel in CSS px. Half the visible track height. */
const STICK_TRAVEL_PX = 60;
/** Radial dead zone in normalized stick units. Re-scaled to [0,1] beyond
 *  the dead zone to avoid a visible step at the edge. Standard 15% (Unity
 *  default 12.5%, mobile shooters trend 15-20%). */
const STICK_DEADZONE = 0.15;
/** Snap-to-zero ease (seconds) on release. Keeps the drop smooth so the
 *  racer doesn't get a one-frame thrust cliff that audio/particles would
 *  read as a click. */
const STICK_RELEASE_SECONDS = 0.12;

/**
 * Twin-stick differential thrust controller for touch devices. Each stick
 * is a vertical-only slider pinned to a corner of the screen. Drag up =
 * forward thrust on that engine, drag down = reverse, release = snap to
 * zero. The anchor floats if the player drags past max travel so they
 * never have to pull their thumb all the way back.
 *
 * Outputs `leftThrust` / `rightThrust` in [-1, +1] via `getRaw()`. The
 * legacy throttle/brake/steer fields are pinned to 0 so the per-engine
 * code path in Physics.step takes over.
 */
export class StickInput {
  readonly state: InputState = {
    throttle: 0,
    brake: 0,
    steer: 0,
    leftThrust: 0,
    rightThrust: 0,
  };
  private readonly opts: StickInputOptions;
  private readonly cleanups: Array<() => void> = [];
  private readonly sticks: Stick[] = [];
  private active = false;

  constructor(opts: StickInputOptions = {}) {
    this.opts = opts;
  }

  attach(): void {
    if (this.active) return;
    for (const side of ['left', 'right'] as const) {
      const root = document.getElementById(`touch-stick-${side}`);
      const thumb = root?.querySelector<HTMLElement>('.touch-stick-thumb');
      if (!root || !thumb) continue;

      const stick: Stick = {
        side,
        root,
        thumb,
        pointerId: null,
        anchorY: 0,
        travelPx: 0,
        releaseAt: null,
        releaseStartTravel: 0,
      };
      this.sticks.push(stick);

      const onDown = (e: PointerEvent): void => this.handleDown(stick, e);
      const onMove = (e: PointerEvent): void => this.handleMove(stick, e);
      const onUp = (e: PointerEvent): void => this.handleUp(stick, e);
      // iOS long-press magnifier is gated on the touch-event layer, not
      // the synthesised pointer-event layer. preventDefault on the
      // pointer event won't cancel it. A finger holding the stick still
      // (steady throttle) reads as a long-press to the gesture
      // recogniser, which can latch onto the FWD/REV labels. Cancelling
      // touchstart kills the gesture before it starts.
      const onTouchStart = (e: TouchEvent): void => e.preventDefault();
      const onCtx = (e: Event): void => e.preventDefault();

      root.addEventListener('pointerdown', onDown);
      root.addEventListener('pointermove', onMove);
      root.addEventListener('pointerup', onUp);
      root.addEventListener('pointercancel', onUp);
      root.addEventListener('lostpointercapture', onUp);
      root.addEventListener('touchstart', onTouchStart, { passive: false });
      root.addEventListener('contextmenu', onCtx);

      this.cleanups.push(() => {
        root.removeEventListener('pointerdown', onDown);
        root.removeEventListener('pointermove', onMove);
        root.removeEventListener('pointerup', onUp);
        root.removeEventListener('pointercancel', onUp);
        root.removeEventListener('lostpointercapture', onUp);
        root.removeEventListener('touchstart', onTouchStart);
        root.removeEventListener('contextmenu', onCtx);
      });
    }

    // Window-level safety nets, same as TouchInput. Mobile browsers
    // occasionally swallow pointerup when another gesture takes over.
    const onWindowUp = (e: PointerEvent): void => {
      for (const s of this.sticks) {
        if (s.pointerId === e.pointerId) this.handleUp(s, e);
      }
    };
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') this.reset();
    };
    const onBlur = (): void => this.reset();
    window.addEventListener('pointerup', onWindowUp, { passive: true });
    window.addEventListener('pointercancel', onWindowUp, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);
    this.cleanups.push(() => {
      window.removeEventListener('pointerup', onWindowUp);
      window.removeEventListener('pointercancel', onWindowUp);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
    });

    this.active = true;
  }

  detach(): void {
    if (!this.active) return;
    for (const c of this.cleanups) c();
    this.cleanups.length = 0;
    this.sticks.length = 0;
    this.active = false;
    this.reset();
  }

  /** Snapshot for InputManager.update() to merge in. */
  getRaw(): Readonly<InputState> {
    return this.state;
  }

  /** Force-release everything (used when paused / loses focus). */
  reset(): void {
    for (const s of this.sticks) {
      if (s.pointerId !== null) {
        try { s.root.releasePointerCapture(s.pointerId); } catch { /* ignore */ }
      }
      s.pointerId = null;
      s.travelPx = 0;
      s.releaseAt = null;
      s.root.classList.remove('is-active');
      this.applyThumbTransform(s);
    }
    this.recompute();
  }

  /** Drive the snap-to-zero spring after release. Called once per frame. */
  update(now: number = performance.now()): void {
    let changed = false;
    for (const s of this.sticks) {
      if (s.pointerId !== null) continue;
      if (s.releaseAt === null) continue;
      const t = (now - s.releaseAt) / 1000 / STICK_RELEASE_SECONDS;
      if (t >= 1) {
        s.travelPx = 0;
        s.releaseAt = null;
        this.applyThumbTransform(s);
        changed = true;
        continue;
      }
      // Cubic ease-out from the travel captured at release.
      const ease = 1 - Math.pow(1 - t, 3);
      s.travelPx = s.releaseStartTravel * (1 - ease);
      this.applyThumbTransform(s);
      changed = true;
    }
    if (changed) this.recompute();
  }

  private handleDown(stick: Stick, e: PointerEvent): void {
    e.preventDefault();
    // Release any prior capture so a second pointerdown (multi-touch
    // overlap, replayed event) can't orphan the original pointerId and
    // leave the stick stuck active. Same guard as TouchInput.
    if (stick.pointerId !== null && stick.pointerId !== e.pointerId) {
      try { stick.root.releasePointerCapture(stick.pointerId); } catch { /* ignore */ }
    }
    stick.pointerId = e.pointerId;
    stick.anchorY = e.clientY;
    stick.travelPx = 0;
    stick.releaseAt = null;
    stick.root.classList.add('is-active');
    try { stick.root.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    this.opts.onAnyStart?.();
    this.applyThumbTransform(stick);
    this.recompute();
  }

  private handleMove(stick: Stick, e: PointerEvent): void {
    if (stick.pointerId !== e.pointerId) return;
    e.preventDefault();
    // Drag UP (smaller clientY) = positive travel = forward thrust.
    let travel = stick.anchorY - e.clientY;
    // Floating origin: if the player drags past max travel in either
    // direction, slide the anchor along so they never have to pull all
    // the way back. Same trick PUBG / Genshin use on the move stick.
    if (travel > STICK_TRAVEL_PX) {
      stick.anchorY -= travel - STICK_TRAVEL_PX;
      travel = STICK_TRAVEL_PX;
    } else if (travel < -STICK_TRAVEL_PX) {
      stick.anchorY -= travel + STICK_TRAVEL_PX;
      travel = -STICK_TRAVEL_PX;
    }
    stick.travelPx = travel;
    this.applyThumbTransform(stick);
    this.recompute();
  }

  private handleUp(stick: Stick, e: PointerEvent): void {
    if (stick.pointerId !== null && stick.pointerId !== e.pointerId) return;
    if (stick.pointerId !== null) {
      try { stick.root.releasePointerCapture(stick.pointerId); } catch { /* ignore */ }
    }
    stick.pointerId = null;
    stick.releaseAt = performance.now();
    stick.releaseStartTravel = stick.travelPx;
    stick.root.classList.remove('is-active');
    // Don't immediately zero travelPx — let update() ease it down.
    this.recompute();
  }

  private applyThumbTransform(stick: Stick): void {
    // Drag up = thumb moves up = transform translateY negative.
    stick.thumb.style.transform = `translate(-50%, calc(-50% + ${(-stick.travelPx).toFixed(1)}px))`;
    const mag = Math.min(1, Math.abs(stick.travelPx) / STICK_TRAVEL_PX);
    stick.root.style.setProperty('--stick-magnitude', mag.toFixed(3));
    stick.root.classList.toggle('is-forward', stick.travelPx > 0);
    stick.root.classList.toggle('is-reverse', stick.travelPx < 0);
  }

  private recompute(): void {
    let l = 0;
    let r = 0;
    for (const s of this.sticks) {
      const v = applyDeadzone(s.travelPx / STICK_TRAVEL_PX, STICK_DEADZONE);
      if (s.side === 'left') l = v;
      else r = v;
    }
    this.state.leftThrust = l;
    this.state.rightThrust = r;
    // Pin legacy axes so the per-engine code path is the only driver.
    this.state.throttle = 0;
    this.state.brake = 0;
    this.state.steer = 0;
  }
}

/**
 * Radial dead zone with rescale: input ∈ [-1,+1]. Anything inside the
 * dead zone reads as 0; anything outside is rescaled to [0,1] starting
 * at the dead-zone edge. Avoids the visible step at the edge that a
 * pure clamp would produce. See "Doing Thumbstick Dead Zones Right",
 * gamedeveloper.com.
 */
export function applyDeadzone(value: number, deadzone: number): number {
  const sign = Math.sign(value);
  const mag = Math.abs(value);
  if (mag <= deadzone) return 0;
  const scaled = (mag - deadzone) / (1 - deadzone);
  return sign * Math.min(1, scaled);
}
